// Feedback Widget UI

import { TriaglyConfig } from './types';

export class FeedbackWidget {
  private config: TriaglyConfig;
  private container: HTMLElement | null = null;
  private isOpen: boolean = false;
  private previouslyFocusedElement: HTMLElement | null = null;
  private focusableElements: HTMLElement[] = [];

  constructor(config: TriaglyConfig) {
    this.config = config;
  }

  /**
   * Initialize the widget
   */
  init(): void {
    this.createButton();
    this.injectStyles();
    // Load Turnstile script if configured
    if (this.config.turnstileSiteKey) {
      this.loadTurnstileScript();
    }
  }

  /**
   * Load Cloudflare Turnstile script dynamically
   */
  private loadTurnstileScript(): void {
    // Check if already loaded
    if ((window as any).turnstile || document.querySelector('script[src*="turnstile"]')) {
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }

  /**
   * Create the feedback button
   */
  private createButton(): void {
    const button = document.createElement('button');
    button.id = 'triagly-button';
    button.className = 'triagly-button';
    
    // Button shape
    const shape = this.config.buttonShape || 'rounded';
    button.classList.add(`triagly-shape-${shape}`);
    
    // Button orientation
    const orientation = this.config.orientation || 'horizontal';
    button.classList.add(`triagly-orientation-${orientation}`);
    
    // Speech bubble SVG icon
    const speechBubbleIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor" class="triagly-icon"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/></svg>`;
    const largeIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="currentColor" class="triagly-icon"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/></svg>`;

    // Handle button text based on shape
    const fullText = this.config.buttonText || 'Feedback';
    if (shape === 'circular') {
      button.innerHTML = largeIcon;
      button.setAttribute('aria-label', fullText);
    } else if (shape === 'expandable') {
      // Expandable starts with icon, expands to full text on hover
      button.innerHTML = `<span class="triagly-btn-icon">${largeIcon}</span><span class="triagly-btn-text"> ${this.config.buttonText || 'Feedback'}</span>`;
      button.setAttribute('aria-label', fullText);
    } else {
      // Default: icon + text
      button.innerHTML = `${speechBubbleIcon}<span class="triagly-btn-label">${fullText}</span>`;
    }
    
    button.onclick = () => this.toggle();

    // Position button
    const position = this.config.position || 'bottom-right';
    button.classList.add(`triagly-${position}`);

    // For expandable buttons, set expansion direction based on position
    if (shape === 'expandable') {
      if (position.includes('right')) {
        button.classList.add('triagly-expand-left');
      } else if (position.includes('left')) {
        button.classList.add('triagly-expand-right');
      }
    }

    // Apply custom offsets if provided
    if (this.config.offsetX) {
      if (position.includes('right')) {
        button.style.right = this.config.offsetX;
      } else if (position.includes('left')) {
        button.style.left = this.config.offsetX;
      }
    }
    if (this.config.offsetY) {
      if (position.includes('top')) {
        button.style.top = this.config.offsetY;
      } else if (position.includes('bottom')) {
        button.style.bottom = this.config.offsetY;
      }
    }

    document.body.appendChild(button);
  }

  /**
   * Toggle widget visibility
   */
  toggle(): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Open the widget
   */
  open(): void {
    if (this.isOpen) return;

    // Store currently focused element to restore later
    this.previouslyFocusedElement = document.activeElement as HTMLElement;

    this.container = this.createContainer();
    document.body.appendChild(this.container);
    this.isOpen = true;

    // Call onOpen callback
    if (this.config.onOpen) {
      this.config.onOpen();
    }

    // Set up keyboard and focus after DOM is ready
    setTimeout(() => {
      // Set up keyboard event listener
      this.setupKeyboardEvents();

      // Set up focus trap
      this.setupFocusTrap();

      // Focus on description field
      const descInput = this.container?.querySelector('#triagly-description') as HTMLTextAreaElement;
      descInput?.focus();
    }, 0);
  }

  /**
   * Close the widget
   */
  close(reason?: 'cancel' | 'dismiss' | 'overlay' | 'programmatic'): void {
    if (!this.isOpen || !this.container) return;

    // Clean up tab handler (must match capture phase used when adding)
    const tabHandler = (this.container as any)._tabHandler;
    if (tabHandler) {
      document.removeEventListener('keydown', tabHandler, true);
    }

    this.container.remove();
    this.container = null;
    this.isOpen = false;

    // Restore focus to previously focused element
    if (this.previouslyFocusedElement) {
      this.previouslyFocusedElement.focus();
      this.previouslyFocusedElement = null;
    }

    // Call specific callbacks based on reason
    if (reason === 'cancel' && this.config.onCancel) {
      this.config.onCancel();
    } else if (reason === 'dismiss' && this.config.onDismiss) {
      this.config.onDismiss();
    } else if (reason === 'overlay' && this.config.onOverlayClick) {
      this.config.onOverlayClick();
    }

    // Always call general onClose callback (backward compatible)
    if (this.config.onClose) {
      this.config.onClose();
    }
  }

  /**
   * Create the widget container
   */
  private createContainer(): HTMLElement {
    const overlay = document.createElement('div');
    overlay.className = 'triagly-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Send feedback');
    overlay.onclick = (e) => {
      if (e.target === overlay) this.close('overlay');
    };

    const modal = document.createElement('div');
    modal.className = 'triagly-modal';
    modal.setAttribute('role', 'document');

    const header = document.createElement('div');
    header.className = 'triagly-header';
    header.innerHTML = `
      <button type="button" class="triagly-close" aria-label="Close feedback form">×</button>
    `;

    const closeBtn = header.querySelector('.triagly-close');
    closeBtn?.addEventListener('click', () => this.close('dismiss'));

    const form = document.createElement('form');
    form.className = 'triagly-form';
    form.innerHTML = `
      <div class="triagly-field">
        <label for="triagly-description">What's on your mind?</label>
        <textarea
          id="triagly-description"
          required
          rows="5"
          placeholder="${this.config.placeholderText || 'Describe what happened...'}"
        ></textarea>
      </div>

      <div class="triagly-field">
        <label for="triagly-name">Name (optional)</label>
        <input
          type="text"
          id="triagly-name"
          placeholder="Your name"
        />
      </div>

      <div class="triagly-field">
        <label for="triagly-email">Email (optional)</label>
        <input
          type="email"
          id="triagly-email"
          placeholder="your@email.com"
        />
      </div>

      ${this.config.turnstileSiteKey ? `
      <div class="triagly-field triagly-turnstile">
        <div class="cf-turnstile" data-sitekey="${this.config.turnstileSiteKey}" data-theme="light"></div>
      </div>
      ` : ''}

      <div class="triagly-actions">
        <button type="button" class="triagly-btn-secondary" id="triagly-cancel" aria-label="Cancel and close feedback form">
          Cancel
        </button>
        <button type="submit" class="triagly-btn-primary" aria-label="Submit feedback">
          Send Feedback
        </button>
      </div>

      <div class="triagly-status" id="triagly-status" role="status" aria-live="polite"></div>
    `;

    const cancelBtn = form.querySelector('#triagly-cancel');
    cancelBtn?.addEventListener('click', () => this.close('cancel'));

    form.onsubmit = (e) => {
      e.preventDefault();
      this.handleSubmit(form);
    };

    const footer = document.createElement('div');
    footer.className = 'triagly-footer';
    footer.innerHTML = `
      <a href="https://triagly.com" target="_blank" rel="noopener noreferrer" class="triagly-branding">
        Powered by <strong>Triagly</strong>
      </a>
    `;

    modal.appendChild(header);
    modal.appendChild(form);
    modal.appendChild(footer);
    overlay.appendChild(modal);

    // Render Turnstile widget if available
    if (this.config.turnstileSiteKey) {
      setTimeout(() => {
        this.renderTurnstileWidget(form);
      }, 100);
    }

    return overlay;
  }

  /**
   * Render Cloudflare Turnstile widget
   */
  private renderTurnstileWidget(form: HTMLFormElement): void {
    const turnstileContainer = form.querySelector('.cf-turnstile');
    if (!turnstileContainer) return;

    // Check if Turnstile script is loaded
    if (!(window as any).turnstile) {
      console.warn('Triagly: Turnstile script not loaded. Please include: <script src="https://challenges.cloudflare.com/turnstile/v0/api.js" async defer></script>');
      return;
    }

    try {
      const widgetId = (window as any).turnstile.render(turnstileContainer, {
        sitekey: this.config.turnstileSiteKey,
        theme: this.config.theme === 'dark' ? 'dark' : 'light',
        callback: (token: string) => {
          // Store token in a data attribute for easy retrieval
          turnstileContainer.setAttribute('data-turnstile-response', token);
          turnstileContainer.setAttribute('data-widget-id', widgetId);
        },
        'error-callback': () => {
          console.error('Triagly: Turnstile widget error');
        },
        'expired-callback': () => {
          // Clear stored token when it expires
          turnstileContainer.removeAttribute('data-turnstile-response');
        },
      });

      turnstileContainer.setAttribute('data-widget-id', widgetId);
    } catch (error) {
      console.error('Triagly: Failed to render Turnstile widget:', error instanceof Error ? error.message : 'Unknown error');
    }
  }

  /**
   * Handle form submission
   */
  private async handleSubmit(form: HTMLFormElement): Promise<void> {
    const descInput = form.querySelector('#triagly-description') as HTMLTextAreaElement;
    const nameInput = form.querySelector('#triagly-name') as HTMLInputElement;
    const emailInput = form.querySelector('#triagly-email') as HTMLInputElement;
    const statusDiv = form.querySelector('#triagly-status') as HTMLDivElement;
    const submitBtn = form.querySelector('button[type="submit"]') as HTMLButtonElement;
    const turnstileContainer = form.querySelector('.cf-turnstile');

    // Disable form
    submitBtn.disabled = true;
    submitBtn.textContent = 'Sending...';

    try {
      // Get Turnstile token if widget is present
      let turnstileToken: string | undefined;
      if (turnstileContainer) {
        turnstileToken = turnstileContainer.getAttribute('data-turnstile-response') || undefined;
      }

      const data = {
        description: descInput.value.trim(),
        reporterName: nameInput.value.trim() || undefined,
        reporterEmail: emailInput.value.trim() || undefined,
        turnstileToken,
      };

      // Create a promise that waits for actual submission result
      const submissionPromise = new Promise<void>((resolve, reject) => {
        const handleSuccess = () => {
          document.removeEventListener('triagly:success', handleSuccess);
          document.removeEventListener('triagly:error', handleError);
          resolve();
        };

        const handleError = (e: Event) => {
          document.removeEventListener('triagly:success', handleSuccess);
          document.removeEventListener('triagly:error', handleError);
          reject((e as CustomEvent).detail);
        };

        document.addEventListener('triagly:success', handleSuccess, { once: true });
        document.addEventListener('triagly:error', handleError, { once: true });

        // Set a timeout in case events don't fire
        setTimeout(() => {
          document.removeEventListener('triagly:success', handleSuccess);
          document.removeEventListener('triagly:error', handleError);
          reject(new Error('Submission timeout'));
        }, 30000); // 30 second timeout
      });

      // Dispatch custom event for parent to handle
      const event = new CustomEvent('triagly:submit', {
        detail: data,
        bubbles: true,
      });
      document.dispatchEvent(event);

      // Wait for actual submission result
      await submissionPromise;

      // Show success
      statusDiv.className = 'triagly-status triagly-success';
      statusDiv.textContent = this.config.successMessage || 'Feedback sent successfully!';

      // Close after delay
      setTimeout(() => {
        this.close();
      }, 2000);
    } catch (error) {
      // Show error with actual error message
      statusDiv.className = 'triagly-status triagly-error';
      const errorMessage = error instanceof Error ? error.message :
        (this.config.errorMessage || 'Failed to send feedback. Please try again.');
      statusDiv.textContent = errorMessage;

      // Re-enable form
      submitBtn.disabled = false;
      submitBtn.textContent = 'Send Feedback';
    }
  }

  /**
   * Inject widget styles
   */
  private injectStyles(): void {
    if (document.getElementById('triagly-styles')) return;

    const style = document.createElement('style');
    style.id = 'triagly-styles';
    style.textContent = `
      .triagly-button {
        position: fixed;
        z-index: 999999;
        padding: 12px 20px;
        background: var(--triagly-button-bg, #18181b);
        color: var(--triagly-button-text, #ffffff);
        border: none;
        border-radius: var(--triagly-button-radius, 8px);
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        box-shadow: var(--triagly-button-shadow, 0 4px 12px rgba(0, 0, 0, 0.15));
        transition: all 0.2s;
        display: inline-flex;
        align-items: center;
        gap: 8px;
      }

      .triagly-button:hover {
        background: var(--triagly-button-bg-hover, #27272a);
        transform: translateY(-2px);
        box-shadow: var(--triagly-button-shadow-hover, 0 6px 16px rgba(0, 0, 0, 0.2));
      }

      .triagly-button .triagly-icon {
        flex-shrink: 0;
      }

      .triagly-button .triagly-btn-label {
        line-height: 1;
      }

      /* Prevent expandable buttons from shifting on hover */
      .triagly-button.triagly-shape-expandable:hover {
        transform: translateY(0) !important;
      }

      .triagly-bottom-right { bottom: 20px; right: 20px; }
      .triagly-bottom-left { bottom: 20px; left: 20px; }
      .triagly-top-right { top: 20px; right: 20px; }
      .triagly-top-left { top: 20px; left: 20px; }

      /* Edge-aligned positions (0 offset from edges) */
      .triagly-edge-bottom-right { bottom: 0; right: 0; }
      .triagly-edge-bottom-left { bottom: 0; left: 0; }
      .triagly-edge-top-right { top: 0; right: 0; }
      .triagly-edge-top-left { top: 0; left: 0; }
      .triagly-edge-right { top: 50%; right: 0; transform: translateY(-50%); }
      .triagly-edge-left { top: 50%; left: 0; transform: translateY(-50%); }
      .triagly-edge-top { top: 0; left: 50%; transform: translateX(-50%); }
      .triagly-edge-bottom { bottom: 0; left: 50%; transform: translateX(-50%); }

      /* Button shapes */
      .triagly-shape-rounded {
        border-radius: var(--triagly-button-radius, 8px);
      }
      .triagly-shape-circular {
        border-radius: 50%;
        width: 60px;
        height: 60px;
        padding: 0;
        font-size: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .triagly-shape-square {
        border-radius: 0;
      }
      .triagly-shape-pill {
        border-radius: 30px;
      }
      .triagly-shape-expandable {
        border-radius: 50%;
        width: 60px;
        height: 60px;
        min-width: 60px;
        padding: 0;
        font-size: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1),
                    border-radius 0.3s cubic-bezier(0.4, 0, 0.2, 1),
                    padding 0.3s cubic-bezier(0.4, 0, 0.2, 1),
                    background 0.2s,
                    box-shadow 0.2s;
        white-space: nowrap;
      }
      
      /* Expansion direction - expands left for right-positioned buttons */
      .triagly-shape-expandable.triagly-expand-left {
        flex-direction: row-reverse;
      }
      
      /* Expansion direction - expands right for left-positioned buttons */
      .triagly-shape-expandable.triagly-expand-right {
        flex-direction: row;
      }
      
      .triagly-shape-expandable .triagly-btn-icon {
        display: inline-block;
        flex-shrink: 0;
        transition: margin 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      }
      .triagly-shape-expandable .triagly-btn-text {
        display: inline-block;
        width: 0;
        opacity: 0;
        overflow: hidden;
        font-size: 14px;
        font-weight: 500;
        transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1),
                    opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      }
      
      /* Hover state */
      .triagly-shape-expandable:hover {
        width: auto;
        min-width: auto;
        padding: 12px 20px;
        border-radius: 30px;
        background: var(--triagly-button-bg-hover, #27272a);
        box-shadow: var(--triagly-button-shadow-hover, 0 6px 16px rgba(0, 0, 0, 0.2));
      }
      .triagly-shape-expandable:hover .triagly-btn-text {
        width: auto;
        opacity: 1;
      }

      /* Button orientations */
      .triagly-orientation-horizontal {
        writing-mode: horizontal-tb;
      }
      .triagly-orientation-vertical {
        writing-mode: vertical-rl;
        text-orientation: mixed;
      }

      .triagly-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: var(--triagly-overlay-bg, rgba(0, 0, 0, 0.5));
        z-index: 1000000;
        display: flex;
        align-items: center;
        justify-content: center;
        animation: triagly-fadeIn 0.2s;
      }

      @keyframes triagly-fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      .triagly-modal {
        background: var(--triagly-modal-bg, #ffffff);
        border-radius: var(--triagly-modal-radius, 12px);
        width: 90%;
        max-width: var(--triagly-modal-max-width, 500px);
        max-height: 90vh;
        overflow-y: auto;
        box-shadow: var(--triagly-modal-shadow, 0 20px 60px rgba(0, 0, 0, 0.3));
        animation: triagly-slideUp 0.3s;
      }

      @keyframes triagly-slideUp {
        from {
          opacity: 0;
          transform: translateY(20px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      .triagly-header {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        padding: 8px 12px 0;
        background: var(--triagly-header-bg, #ffffff);
      }

      .triagly-close {
        background: none;
        border: none;
        font-size: 28px;
        color: #6b7280;
        cursor: pointer;
        padding: 0;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 6px;
        transition: all 0.2s;
      }

      .triagly-close:hover {
        background: #f3f4f6;
        color: #111827;
      }

      .triagly-form {
        padding: 24px;
        background: var(--triagly-form-bg, #ffffff);
      }

      .triagly-field {
        margin-bottom: 16px;
      }

      .triagly-field label {
        display: block;
        margin-bottom: 6px;
        font-size: 14px;
        font-weight: 500;
        color: var(--triagly-label-text, #374151);
      }

      .triagly-field input,
      .triagly-field textarea {
        width: 100%;
        padding: 10px 12px;
        background: var(--triagly-input-bg, #ffffff);
        border: 1px solid var(--triagly-input-border, #d1d5db);
        border-radius: var(--triagly-input-radius, 6px);
        color: var(--triagly-input-text, #111827);
        font-size: 14px;
        font-family: inherit;
        transition: border-color 0.2s;
        box-sizing: border-box;
      }

      .triagly-field input:focus,
      .triagly-field textarea:focus {
        outline: none;
        border-color: var(--triagly-input-border-focus, #a1a1aa);
        box-shadow: 0 0 0 2px rgba(161, 161, 170, 0.15);
      }

      /* Focus visible styles for accessibility */
      .triagly-button:focus-visible,
      .triagly-field input:focus-visible,
      .triagly-field textarea:focus-visible,
      .triagly-btn-primary:focus-visible,
      .triagly-btn-secondary:focus-visible,
      .triagly-close:focus-visible {
        outline: 2px solid #a1a1aa;
        outline-offset: 2px;
      }

      .triagly-turnstile {
        display: flex;
        justify-content: center;
        margin: 8px 0;
      }

      .triagly-actions {
        display: flex;
        gap: 12px;
        margin-top: 24px;
      }

      .triagly-btn-primary,
      .triagly-btn-secondary {
        flex: 1;
        padding: 10px 16px;
        border-radius: var(--triagly-btn-radius, 6px);
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s;
        border: none;
      }

      .triagly-btn-primary {
        background: var(--triagly-btn-primary-bg, #18181b);
        color: var(--triagly-btn-primary-text, #ffffff);
      }

      .triagly-btn-primary:hover:not(:disabled) {
        background: var(--triagly-btn-primary-bg-hover, #27272a);
      }

      .triagly-btn-primary:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      .triagly-btn-secondary {
        background: var(--triagly-btn-secondary-bg, #f3f4f6);
        color: var(--triagly-btn-secondary-text, #374151);
      }

      .triagly-btn-secondary:hover {
        background: var(--triagly-btn-secondary-bg-hover, #e5e7eb);
      }

      .triagly-status {
        margin-top: 16px;
        padding: 12px;
        border-radius: 6px;
        font-size: 14px;
        display: none;
      }

      .triagly-status.triagly-success {
        display: block;
        background: var(--triagly-success-bg, #d1fae5);
        color: var(--triagly-success-text, #065f46);
      }

      .triagly-status.triagly-error {
        display: block;
        background: var(--triagly-error-bg, #fee2e2);
        color: var(--triagly-error-text, #991b1b);
      }

      .triagly-footer {
        padding: 12px 24px 16px;
        text-align: right;
        border-top: 1px solid var(--triagly-footer-border, #e5e7eb);
        background: var(--triagly-footer-bg, #f9fafb);
        border-radius: 0 0 var(--triagly-modal-radius, 12px) var(--triagly-modal-radius, 12px);
      }

      .triagly-branding {
        font-size: 12px;
        color: var(--triagly-footer-text, #6b7280);
        text-decoration: none;
        transition: color 0.2s;
      }

      .triagly-branding:hover {
        color: var(--triagly-footer-text-hover, #18181b);
      }

      .triagly-branding strong {
        font-weight: 600;
      }
    `;

    document.head.appendChild(style);
  }

  /**
   * Set up keyboard event handlers
   */
  private setupKeyboardEvents(): void {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Close on Escape key
      if (e.key === 'Escape' && this.isOpen) {
        e.preventDefault();
        this.close('dismiss');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    
    // Store handler for cleanup
    if (this.container) {
      (this.container as any)._keydownHandler = handleKeyDown;
    }
  }

  /**
   * Set up focus trap to keep focus within modal
   */
  private setupFocusTrap(): void {
    if (!this.container) return;

    // Get all focusable elements
    const modal = this.container.querySelector('.triagly-modal');
    if (!modal) return;

    const focusableSelector =
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    this.focusableElements = Array.from(
      modal.querySelectorAll(focusableSelector)
    ) as HTMLElement[];

    if (this.focusableElements.length === 0) {
      console.warn('Triagly: No focusable elements found in modal');
      return;
    }

    // Handle Tab key to trap focus - must prevent default BEFORE focus moves
    const handleTab = (e: Event) => {
      const keyEvent = e as KeyboardEvent;
      if (keyEvent.key !== 'Tab') return;

      // Only handle if focus is within our modal
      if (!this.container?.contains(document.activeElement)) return;

      const firstFocusable = this.focusableElements[0];
      const lastFocusable = this.focusableElements[this.focusableElements.length - 1];

      if (keyEvent.shiftKey) {
        // Shift + Tab - moving backwards
        if (document.activeElement === firstFocusable) {
          keyEvent.preventDefault();
          lastFocusable?.focus();
        }
      } else {
        // Tab - moving forwards
        if (document.activeElement === lastFocusable) {
          keyEvent.preventDefault();
          firstFocusable?.focus();
        }
      }
    };

    // Use capture phase to intercept Tab before browser handles it
    document.addEventListener('keydown', handleTab as EventListener, true);
    (this.container as any)._tabHandler = handleTab;
  }

  /**
   * Destroy the widget
   */
  destroy(): void {
    // Clean up event listeners
    if (this.container) {
      const keydownHandler = (this.container as any)._keydownHandler;
      if (keydownHandler) {
        document.removeEventListener('keydown', keydownHandler);
      }

      const tabHandler = (this.container as any)._tabHandler;
      if (tabHandler) {
        document.removeEventListener('keydown', tabHandler, true);
      }
    }

    this.close();
    document.getElementById('triagly-button')?.remove();
    document.getElementById('triagly-styles')?.remove();
  }
}
